import React from "react";
import { Link } from "react-router-dom";
import { CATEGORY_ICONS, LABEL_STYLE } from "@/lib/categories";
import { useTranslation } from "react-i18next";
import { useServiceI18n } from "@/lib/serviceI18n";
import { ArrowUpRight } from "lucide-react";

export const CategoryCard = ({ category, compact = false }) => {
  const { t } = useTranslation();
  const { catName, catDesc, subName } = useServiceI18n();
  const Icon = CATEGORY_ICONS[category.icon] || CATEGORY_ICONS.sparkles;
  const popular = category.subcategories.filter((s) => s.labels.includes("populair"));

  return (
    <Link
      to={`/diensten/${category.id}`}
      className="group relative bg-white border border-[#EAE6DF] rounded-md p-6 md:p-7 flex flex-col h-full transition-all duration-300 hover:border-[#0A1128] hover:shadow-[0_16px_48px_rgba(10,17,40,0.08)] hover:-translate-y-1"
      data-testid={`category-card-${category.id}`}
    >
      {/* Top row */}
      <div className="flex items-start justify-between">
        <div className="w-11 h-11 rounded-md bg-[#F3F0EA] text-[#0A1128] flex items-center justify-center transition-all group-hover:bg-[#0A1128] group-hover:text-[#F5A623]">
          <Icon size={20} strokeWidth={1.75} />
        </div>
        <span className="eyebrow tabular">
          {String(category.subcategories.length).padStart(2, "0")}
        </span>
      </div>

      {/* Title */}
      <h3 className="font-heading text-xl md:text-[22px] font-medium text-[#0A1128] mt-6 tracking-tight leading-tight">
        {catName(category)}
      </h3>

      {!compact && (
        <p className="text-[13px] text-[#0A1128]/55 mt-2.5 leading-relaxed flex-1">{catDesc(category)}</p>
      )}

      {popular.length > 0 && (
        <div className="mt-5 flex flex-wrap gap-1.5">
          {popular.slice(0, 3).map((s) => (
            <span key={s.id} className="pill text-[10px]">{subName(s)}</span>
          ))}
          {popular.length > 3 && <span className="pill text-[10px]">+{popular.length - 3}</span>}
        </div>
      )}

      <div className="mt-6 pt-5 border-t border-[#EAE6DF] flex items-center justify-between">
        <span className="text-[12px] font-semibold text-[#0A1128]/70 group-hover:text-[#F5A623] transition-colors uppercase tracking-[0.14em]">
          {t("services.view_all")}
        </span>
        <ArrowUpRight size={16} className="text-[#0A1128]/40 transition-all group-hover:text-[#F5A623] group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </div>
    </Link>
  );
};

export const Label = ({ label }) => {
  const { t } = useTranslation();
  const style = LABEL_STYLE[label] || LABEL_STYLE.populair;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-sm text-[9px] font-bold uppercase tracking-[0.12em] ${style.bg} ${style.text}`}>
      {t(`services.labels.${label}`)}
    </span>
  );
};
